// core/data/videoComments.ts — typed seed for video comments (client-only, in-memory).
//
// Authors are the six seed creators (core/data/creators.ts) the video seed already
// reuses. These are FRONTEND-ONLY display records — they are not guaranteed to exist
// as real backend accounts (prod has no demo authors), so avatars/handles for these
// comments resolve locally via useVideoCommentAuthor (features/videos/comments), the
// same local, network-free path VideoDetailPage already uses to resolve a video's
// creator from useVideos().creators. Do NOT resolve video-comment authors through the
// network-backed useUser hook — see useVideoCommentAuthor.ts's header for why.
//
// Flatten rule: `parentId` always points to a top-level comment id (never to a
// nested comment). `replyingToHandle` provides visual attribution when the user
// replied to an already-nested comment.
//
// PORTED to mobile/src/core/data/videoComments.ts — kept content-identical (pinned by
// videoComments.sync.test.ts, same mechanism as decorativeCover.sync.test.ts). Keep both
// in sync: edit web first, then copy the array verbatim.

import type { VideoComment } from '@/core/models/videoComment';

export const seedVideoComments: VideoComment[] = [
  { id: 'vc-001', videoId: 'vid-01', authorId: 'liberty-mind',
    parentId: null, replyingToHandle: null,
    body: 'This is exactly the case study I needed. Bookmarking to send to a few people.',
    time: '2h', likes: 31, liked: false, deleted: false, edited: false },

  { id: 'vc-002', videoId: 'vid-01', authorId: 'open-mind',
    parentId: null, replyingToHandle: null,
    body: "The section on takedown-first platforms losing trust over time is the whole argument.",
    time: '1h', likes: 14, liked: true, deleted: false, edited: false },

  { id: 'vc-003', videoId: 'vid-01', authorId: 'maya-patel',
    parentId: null, replyingToHandle: null,
    body: 'Would love a follow-up on how this plays out for smaller platforms without the scale.',
    time: '40m', likes: 6, liked: false, deleted: false, edited: false },

  // Nested reply under vc-001 (demonstrates the depth-1 flatten rule).
  { id: 'vc-101', videoId: 'vid-01', authorId: 'freedom-seeker',
    parentId: 'vc-001', replyingToHandle: null,
    body: 'Same, sending this to my team today.',
    time: '1h', likes: 4, liked: false, deleted: false, edited: false },

  { id: 'vc-102', videoId: 'vid-01', authorId: 'jordan-lee',
    parentId: 'vc-001', replyingToHandle: '@FreedomSeeker',
    body: 'Right there with you, this one is worth the full watch.',
    time: '35m', likes: 2, liked: false, deleted: false, edited: false },

  { id: 'vc-004', videoId: 'vid-02', authorId: 'sara-kim',
    parentId: null, replyingToHandle: null,
    body: 'Data portability is the sleeper issue here. Glad someone said it plainly.',
    time: '3h', likes: 19, liked: false, deleted: false, edited: false },

  { id: 'vc-005', videoId: 'vid-02', authorId: 'jordan-lee',
    parentId: null, replyingToHandle: null,
    body: 'Curious what a decentralized identity layer would even look like at scale.',
    time: '2h', likes: 8, liked: false, deleted: false, edited: false },

  { id: 'vc-201', videoId: 'vid-02', authorId: 'freedom-seeker',
    parentId: 'vc-004', replyingToHandle: null,
    body: "Agreed, it's the part everyone glosses over.",
    time: '2h', likes: 3, liked: false, deleted: false, edited: false },

  { id: 'vc-006', videoId: 'vid-03', authorId: 'maya-patel',
    parentId: null, replyingToHandle: null,
    body: 'Finally a grounded explainer that skips the hype cycle.',
    time: '5h', likes: 22, liked: false, deleted: false, edited: false },
];
